import { AlertTriangle, Calendar, Check } from 'lucide-react'
import { useState } from 'react'

import {
  APPOINTMENT_TYPES,
  type AppointmentTypeId,
  type Clinic,
  type ClinicId,
  isAppointmentTypeAvailableAtClinic,
  type PublicPatient,
} from '@snootsvet/shared/portal'

import { CardRadioGroup } from '@/customer/components/card-radio-group'
import { ClinicSelect } from '@/customer/components/clinic-select'
import { ContactSupportAction } from '@/customer/components/contact-support-action'
import { FormField } from '@/customer/components/form/fields'
import { PetPicker } from '@/customer/components/pet/pet-picker'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/customer/components/ui/alert'
import { Badge } from '@/customer/components/ui/badge'
import { Button } from '@/customer/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemContent,
  SelectItemDescription,
  SelectItemLabel,
  SelectTrigger,
  SelectValue,
} from '@/customer/components/ui/select'
import { useListPetAppointmentTypes } from '@/customer/hooks/use-list-pet-appointment-types'
import {
  getAppointmentTypeConfig,
  type MemberVisibleAppointmentTypeId,
} from '@/customer/lib/appointments'
import { cn } from '@/customer/lib/utils'

const SICK_OR_UNWELL_OPTIONS = [
  { value: 'no', label: 'No', icon: <Check className="size-8" />, color: 'green' },
  { value: 'yes', label: 'Yes', icon: <AlertTriangle className="size-8" />, color: 'orange' },
] as const

type MemberAppointmentTypeOption = {
  id: MemberVisibleAppointmentTypeId
  label: string
  description: string
  action: 'self-service' | 'contact-team'
}

type RoutineAppointmentFlowProps = {
  pets: PublicPatient[]
  clinics: Clinic[]
  onProceedToBooking: (values: {
    patientId: string
    appointmentTypeId: MemberVisibleAppointmentTypeId
    clinicId: ClinicId
  }) => void
  alwaysShowPetPicker?: boolean
  defaultClinicId?: ClinicId
  className?: string
}

export function RoutineAppointmentFlow({
  pets,
  clinics,
  onProceedToBooking,
  alwaysShowPetPicker = false,
  defaultClinicId,
  className,
}: RoutineAppointmentFlowProps) {
  const [patientId, setPatientId] = useState(pets.length === 1 ? pets[0].id : null)
  const selectedPet = pets.find((pet) => pet.id === patientId)

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {alwaysShowPetPicker || pets.length > 1 ? (
        <FormField
          label="Who's it for?"
          labelUsesId
          render={({ id }) => (
            <PetPicker
              pets={pets}
              value={selectedPet?.id ?? null}
              onValueChange={setPatientId}
              aria-labelledby={id}
            />
          )}
        />
      ) : null}
      {selectedPet ? (
        <PetBookingChoices
          key={selectedPet.id}
          pet={selectedPet}
          clinics={clinics}
          defaultClinicId={defaultClinicId}
          onProceedToBooking={onProceedToBooking}
        />
      ) : null}
    </div>
  )
}

function PetBookingChoices({
  pet,
  clinics,
  defaultClinicId,
  onProceedToBooking,
}: {
  pet: PublicPatient
  clinics: Clinic[]
  defaultClinicId?: ClinicId
  onProceedToBooking: RoutineAppointmentFlowProps['onProceedToBooking']
}) {
  const [sickOrUnwell, setSickOrUnwell] = useState<'no' | 'yes' | null>(null)
  const [appointmentTypeId, setAppointmentTypeId] = useState<MemberVisibleAppointmentTypeId>()
  const [clinicId, setClinicId] = useState(defaultClinicId)
  const petBookingOptions = useListPetAppointmentTypes(pet.id)
  const firstVisitOnly =
    petBookingOptions.data?.appointmentTypeIds.length === 1 &&
    petBookingOptions.data.appointmentTypeIds[0] === 'first-visit'
  const appointmentTypeOptions: MemberAppointmentTypeOption[] = petBookingOptions.data
    ? APPOINTMENT_TYPES.filter((type) => type.memberBooking !== 'hidden')
        .filter((type) => petBookingOptions.data.appointmentTypeIds.includes(type.id))
        .map((type) => ({
          id: type.id,
          label: getAppointmentTypeConfig(type.id).label,
          description: type.description,
          action: type.memberBooking,
        }))
    : []
  const selectedAppointmentTypeId = firstVisitOnly ? 'first-visit' : appointmentTypeId
  const selectedAppointmentType = appointmentTypeOptions.find(
    (type) => type.id === selectedAppointmentTypeId
  )
  const clinicOptions = clinics.filter(
    (clinic) =>
      !selectedAppointmentTypeId ||
      isAppointmentTypeAvailableAtClinic(selectedAppointmentTypeId, clinic.id)
  )
  const selectedClinic = clinicOptions.find((clinic) => clinic.id === clinicId)
  const canProceed =
    sickOrUnwell === 'no' && selectedAppointmentType?.action === 'self-service' && !!selectedClinic

  if (!petBookingOptions.data) {
    return petBookingOptions.isError ? (
      <Alert variant="warning">
        <AlertTitle>We couldn&apos;t load booking options</AlertTitle>
        <AlertDescription>
          Please try again shortly. If you need to book urgently, contact the care team and
          we&apos;ll sort you out.
        </AlertDescription>
        <AlertAction>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => petBookingOptions.refetch()}
          >
            Try again
          </Button>
          <ContactSupportAction urgent />
        </AlertAction>
      </Alert>
    ) : (
      <p className="text-body-md text-muted-foreground">Loading booking options…</p>
    )
  }

  return (
    <>
      {firstVisitOnly ? <FirstVisitIntro petName={pet.name} /> : null}
      <FormField
        label={`Is ${pet.name} unwell or showing symptoms right now?`}
        labelUsesId
        render={({ id }) => (
          <CardRadioGroup
            value={sickOrUnwell}
            onValueChange={setSickOrUnwell}
            options={SICK_OR_UNWELL_OPTIONS}
            aria-labelledby={id}
          />
        )}
      />
      {sickOrUnwell === 'yes' ? (
        <Alert variant="warning">
          <AlertTitle>Sounds like {pet.name} needs a sick visit</AlertTitle>
          <AlertDescription>
            Let&apos;s get you talking to the care team so we can book{' '}
            {pet.sex === 'male' ? 'him' : pet.sex === 'female' ? 'her' : 'them'} the right kind of
            appointment, fast.
          </AlertDescription>
          <AlertAction>
            <ContactSupportAction urgent />
          </AlertAction>
        </Alert>
      ) : (
        <>
          {firstVisitOnly && sickOrUnwell === 'no' ? (
            <FirstVisitSpecificBookingAlert petName={pet.name} />
          ) : null}
          {!firstVisitOnly ? (
            <FormField
              label="What's the visit for?"
              render={({ id, disabled }) => (
                <AppointmentTypeSelector
                  id={id}
                  options={appointmentTypeOptions}
                  value={selectedAppointmentType?.id}
                  clinicId={clinicId}
                  onValueChange={setAppointmentTypeId}
                  disabled={disabled}
                />
              )}
            />
          ) : null}
          {selectedAppointmentType?.action === 'contact-team' ? (
            <ContactToBookCard typeId={selectedAppointmentType.id} petName={pet.name} />
          ) : (
            <>
              <FormField
                label="Which clinic?"
                render={({ id, disabled }) => (
                  <ClinicSelect
                    id={id}
                    clinics={clinicOptions}
                    value={selectedClinic?.id ?? null}
                    onValueChange={(value) => {
                      const clinic = clinicOptions.find((option) => option.id === value)
                      if (clinic) setClinicId(clinic.id)
                    }}
                    disabled={disabled}
                    placeholder="Select a clinic..."
                  />
                )}
              />
              {!firstVisitOnly && selectedAppointmentType?.action === 'self-service' ? (
                <Alert variant="warning">
                  <AlertTitle>Need something extra?</AlertTitle>
                  <AlertDescription>
                    Want to get a few things sorted in one visit? Please chat with our team before
                    booking so we can book enough time.
                  </AlertDescription>
                  <AlertAction>
                    <ContactSupportAction urgent />
                  </AlertAction>
                </Alert>
              ) : null}
              <Button
                type="button"
                className="w-full"
                disabled={!canProceed}
                onClick={() => {
                  if (!canProceed || !selectedAppointmentType || !selectedClinic) return
                  onProceedToBooking({
                    patientId: pet.id,
                    appointmentTypeId: selectedAppointmentType.id,
                    clinicId: selectedClinic.id,
                  })
                }}
              >
                <Calendar data-icon="inline-start" />
                See availability
              </Button>
            </>
          )}
        </>
      )}
    </>
  )
}

function AppointmentTypeSelector({
  id,
  options,
  value,
  clinicId,
  onValueChange,
  disabled,
}: {
  id: string
  options: MemberAppointmentTypeOption[]
  value: MemberVisibleAppointmentTypeId | undefined
  clinicId: ClinicId | undefined
  onValueChange: (value: MemberVisibleAppointmentTypeId) => void
  disabled: boolean
}) {
  return (
    <Select
      items={options.map((type) => ({ label: type.label, value: type.id }))}
      value={options.some((type) => type.id === value) ? value : null}
      onValueChange={(typeId) => {
        if (typeId) onValueChange(typeId)
      }}
      disabled={disabled}
    >
      <SelectTrigger id={id}>
        <SelectValue
          className="font-bold data-placeholder:font-normal data-placeholder:italic data-placeholder:text-muted-foreground"
          placeholder="Select an appointment type..."
        />
      </SelectTrigger>
      <SelectContent>
        {options.map((type) => {
          const available = !clinicId || isAppointmentTypeAvailableAtClinic(type.id, clinicId)
          return (
            <SelectItem key={type.id} value={type.id} disabled={!available}>
              <SelectItemContent>
                <span className="flex flex-wrap items-center gap-2">
                  <SelectItemLabel
                    className={cn('font-bold', !available && 'text-muted-foreground line-through')}
                  >
                    {type.label}
                  </SelectItemLabel>
                  {!available ? (
                    <Badge variant="neutral">Not available at this clinic</Badge>
                  ) : null}
                </span>
                {type.description ? (
                  <SelectItemDescription className="text-fine-print">
                    {type.description}
                  </SelectItemDescription>
                ) : null}
              </SelectItemContent>
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function FirstVisitIntro({ petName }: { petName: string }) {
  const pet = petName || 'your pet'
  return (
    <div>
      <p className="text-headline-sm">{pet}&apos;s first Snoots visit</p>
      <p className="mt-2 text-body-md">
        We&apos;ll get to know {pet} and make sure they&apos;re set up for care with Snoots.
      </p>
    </div>
  )
}

function FirstVisitSpecificBookingAlert({ petName }: { petName: string }) {
  const pet = petName || 'your pet'
  return (
    <Alert variant="info">
      <AlertTitle>Need to discuss something before {pet}&apos;s first visit?</AlertTitle>
      <AlertDescription>
        <p>
          Please reach out to our care team before booking if you urgently need any of the
          following:
        </p>
        <ul className="list-disc pl-5">
          <li>Animal Health Certificate</li>
          <li>Neutering</li>
          <li>Diagnostics</li>
        </ul>
        <p className="mt-2">This helps us make {pet}&apos;s first visit as smooth as possible.</p>
      </AlertDescription>
      <AlertAction>
        <ContactSupportAction urgent />
      </AlertAction>
    </Alert>
  )
}

function ContactToBookCard({ typeId, petName }: { typeId: AppointmentTypeId; petName: string }) {
  const pet = petName || 'your pet'
  const copy =
    typeId === 'euthanasia'
      ? {
          title: `We're here for you and ${pet}.`,
          body: 'Our care team will talk you through the options and find a time that works, at the clinic or at home.',
        }
      : {
          title: 'Before you book',
          body: `To make the most of ${petName}’s visit, the vet needs a few details beforehand. Please book this appointment directly with our team so we’re ready to provide the right care on the day.`,
        }
  return (
    <Alert variant="warning">
      <AlertTitle>{copy.title}</AlertTitle>
      <AlertDescription>{copy.body}</AlertDescription>
      <AlertAction>
        <ContactSupportAction urgent />
      </AlertAction>
    </Alert>
  )
}
